import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button, controlBoxClasses } from "../../../components/ui";
import type { AppliedCoupon } from "../types/billing.types";

interface CouponInputProps {
  /** What the last quote came back carrying, which is the only coupon there
   *  is — see `useQuote`. `null` for an order with none on it. */
  applied: AppliedCoupon | null;
  /** Sends the typed code up with a fresh quote. Rejects with the server's own
   *  reason, which is what the line under the field prints. */
  onApply: (code: string) => Promise<void>;
  /** Prices the same order again with no code on it. */
  onRemove: () => Promise<void>;
  /** A quote is with the server — the row stands down. */
  busy?: boolean;
  /** The checkout is paying; nothing about the order may change now. */
  locked?: boolean;
  /** Distinguishes the two places this is drawn — the checkout's own row and
   *  the one inside the coupons dialog — so the label and the error line are
   *  tied to the right control when both are in the document. */
  id?: string;
}

/**
 * A code to type, and the two things that can be done with one.
 *
 * ## Nothing here decides anything
 *
 * The field holds what is being typed and nothing else. Whether a code holds,
 * and what it is worth, is `POST /billing/quote/`'s answer — so Apply sends it
 * up and the figures beside it are replaced by what comes back, and a refusal
 * is printed as the server worded it rather than being guessed at here. There
 * is deliberately no list of valid codes on this side to check against: a page
 * that can say "invalid coupon" on its own is a page whose idea of valid can
 * drift from the server's, and the one the buyer reads would be the one that
 * does not count.
 *
 * ## Applied is a different control from empty
 *
 * Once a code holds, the field shows it, goes read-only and the button becomes
 * Remove. That is the honest shape of it: the thing on the order is not a
 * suggestion being edited, it is a discount that is in force, and editing it in
 * place would leave the summary quoting one code while the field reads another.
 *
 * The error clears the moment the field is touched again, because the reason
 * printed was about a code that is no longer the one in the box.
 *
 * Its own component because it is drawn twice — on the checkout beside the
 * summary, and inside the coupons dialog for a workspace that has no coupons
 * to list but has been given a code. Two copies of a control that talks to a
 * payment is how the two would quietly stop agreeing about what a refusal
 * looks like.
 */
const CouponInput: React.FC<CouponInputProps> = ({
  applied,
  onApply,
  onRemove,
  busy = false,
  locked = false,
  id = "coupon-code",
}) => {
  const [code, setCode] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  // What is on the order is what the field shows. Driven from the applied
  // coupon rather than from what was typed, so a code the server accepted in a
  // different case — or one applied from the dialog, which never touched this
  // field at all — is still the code on screen.
  useEffect(() => {
    if (applied) {
      setCode(applied.code);
      setProblem(null);
    }
  }, [applied]);

  const disabled = busy || locked;

  const handleApply = async () => {
    const typed = code.trim();
    if (!typed) return;

    setProblem(null);
    try {
      await onApply(typed);
    } catch (cause) {
      setProblem(
        cause instanceof Error
          ? cause.message
          : "We could not check that code. Please try again."
      );
    }
  };

  const handleRemove = async () => {
    setProblem(null);
    try {
      await onRemove();
      setCode("");
    } catch (cause) {
      setProblem(
        cause instanceof Error
          ? cause.message
          : "We could not update your order. Please try again."
      );
    }
  };

  /** Clearing the box, which is not the same as taking the coupon off — a
   *  coupon in force is removed by the button, and the cross is not drawn
   *  while one is. */
  const clear = () => {
    setCode("");
    setProblem(null);
  };

  return (
    <div>
      {/* The field takes what is left and the button keeps its own width, so a
          long code scrolls inside the box rather than squeezing Apply down to
          its letters. */}
      <div className="flex items-stretch gap-2">
        <div
          className={`${controlBoxClasses(
            !!problem,
            disabled
          )} flex min-w-0 flex-1 items-center gap-2`}
        >
          <input
            id={id}
            value={code}
            disabled={disabled || !!applied}
            placeholder="Enter coupon code"
            aria-label="Coupon code"
            aria-invalid={!!problem}
            aria-describedby={problem ? `${id}-problem` : undefined}
            onChange={(event) => {
              setCode(event.target.value.toUpperCase());
              if (problem) setProblem(null);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              // The checkout is a form with a Pay button on it; Enter in this
              // box means "check this code", not "take my money".
              event.preventDefault();
              if (!applied) void handleApply();
            }}
            className="min-w-0 flex-1 bg-transparent uppercase tracking-wide text-inherit placeholder:normal-case placeholder:tracking-normal placeholder:text-ink-subtle focus:outline-none disabled:cursor-not-allowed"
          />
          {code && !applied && !disabled && (
            <button
              type="button"
              onClick={clear}
              aria-label="Clear the coupon code"
              className="-m-1 flex-shrink-0 p-1 text-ink-subtle transition-colors hover:text-ink"
            >
              <X aria-hidden className="h-4 w-4" />
            </button>
          )}
        </div>

        <Button
          variant="outline"
          size="md"
          loading={busy}
          disabled={locked || (!applied && !code.trim())}
          onClick={applied ? handleRemove : handleApply}
          className="flex-shrink-0"
        >
          {applied ? "Remove" : "Apply"}
        </Button>
      </div>

      {problem && (
        // The server's own words. `role="alert"` because it is the answer to
        // something that was just pressed, and somebody on a screen reader has
        // no other way to know the code did not take.
        <p
          id={`${id}-problem`}
          role="alert"
          className="mt-1.5 text-[12px] 3xl:text-[13px] text-[#D14343]"
        >
          {problem}
        </p>
      )}
    </div>
  );
};

export default CouponInput;
